import { RootState, useAppDispatch } from '@store/store.tsx';
import React, {
  ChangeEvent,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import {
  fetchCategoriesAsync,
  fetchFallbackImagesAsync,
  fetchItemsAsync,
  setCategorySelected,
  setInitialized,
  setOrderby,
  setSearchPrompt,
} from '@store/slices/eshopSlice.tsx';
import { logInfo } from '@tools/LogsHandler.tsx';
import {
  Button,
  Card,
  Divider,
  Drawer,
  Layout,
  Select,
  Skeleton,
  Space,
  Tag,
  Tooltip,
  Tree,
  TreeProps,
  Typography,
} from 'antd';
import {
  AppstoreOutlined,
  ClockCircleOutlined,
  CloseCircleFilled,
  CloseOutlined,
  DownOutlined,
  RightOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { InputRef } from 'antd';
import {
  ExtendedTreeDataNode,
  groupDuplicateRootNames,
  transformCategoriesToTreeData,
} from '@eshop/types/categories.types.ts';
import Search from 'antd/lib/input/Search';
import { stripHtml } from '@tools/utils/common.tsx';
import { buildCategoryPath } from '@eshop/components/categories/categoriesUtils.tsx';
import { useNavigate } from 'react-router-dom';
import { buildUrl } from '@tools/utils/imageUtils.tsx';
import { useDebounce } from 'use-debounce';
import { useThemeMode } from '@theme/hooks/useThemeMode.tsx';

export type CategoriesMenuVariant = 'card' | 'soft-commerce';

interface CategoriesMenuProps {
  variant?: CategoriesMenuVariant;
  withDrawer?: boolean;
}

interface DrawerContextValue {
  isOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  toggleDrawer: () => void;
  registerTrigger: (node: HTMLButtonElement | null) => void;
  drawerId: string;
  drawerTitleId: string;
  isEnabled: boolean;
}

const defaultDrawerContext: DrawerContextValue = {
  isOpen: false,
  openDrawer: () => undefined,
  closeDrawer: () => undefined,
  toggleDrawer: () => undefined,
  registerTrigger: () => undefined,
  drawerId: 'catalogue-filters-drawer',
  drawerTitleId: 'catalogue-filters-drawer-title',
  isEnabled: false,
};

const CategoriesMenuDrawerContext = createContext<DrawerContextValue>(defaultDrawerContext);

export const useCategoriesMenuDrawer = (): DrawerContextValue => useContext(CategoriesMenuDrawerContext);

export const CategoriesMenuDrawerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const previousOverflow = useRef<string | null>(null);

  const registerTrigger = useCallback((node: HTMLButtonElement | null) => {
    triggerRef.current = node;
  }, []);

  const restoreScroll = useCallback(() => {
    if (previousOverflow.current !== null) {
      document.body.style.overflow = previousOverflow.current;
      previousOverflow.current = null;
    }
  }, []);

  const closeDrawer = useCallback(() => {
    setIsOpen(false);
  }, []);

  const openDrawer = useCallback(() => {
    setIsOpen(true);
  }, []);

  const toggleDrawer = useCallback(() => {
    setIsOpen((current) => !current);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleOpenFilters = () => openDrawer();
    window.addEventListener('eshop:open-catalogue-filters', handleOpenFilters);

    return () => {
      window.removeEventListener('eshop:open-catalogue-filters', handleOpenFilters);
    };
  }, [openDrawer]);

  useEffect(() => {
    if (isOpen) {
      if (previousOverflow.current === null) {
        previousOverflow.current = document.body.style.overflow;
      }
      document.body.style.overflow = 'hidden';
      return () => {
        restoreScroll();
      };
    }

    restoreScroll();

    if (triggerRef.current) {
      triggerRef.current.focus({ preventScroll: true });
    }

    return undefined;
  }, [isOpen, restoreScroll]);

  useEffect(() => () => {
    restoreScroll();
  }, [restoreScroll]);

  const contextValue = useMemo<DrawerContextValue>(() => ({
    isOpen,
    openDrawer,
    closeDrawer,
    toggleDrawer,
    registerTrigger,
    drawerId: 'catalogue-filters-drawer',
    drawerTitleId: 'catalogue-filters-drawer-title',
    isEnabled: true,
  }), [closeDrawer, isOpen, openDrawer, registerTrigger, toggleDrawer]);

  return (
    <CategoriesMenuDrawerContext.Provider value={contextValue}>
      {children}
    </CategoriesMenuDrawerContext.Provider>
  );
};

const useMediaQuery = (query: string): boolean => {
  const getMatches = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false;

  const [matches, setMatches] = useState<boolean>(getMatches);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const mediaQueryList = window.matchMedia(query);
    const listener = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    setMatches(mediaQueryList.matches);
    mediaQueryList.addEventListener('change', listener);

    return () => {
      mediaQueryList.removeEventListener('change', listener);
    };
  }, [query]);

  return matches;
};

const ensureIsLeafFlag = (nodes: ExtendedTreeDataNode[]): ExtendedTreeDataNode[] =>
  nodes.map((node) => {
    const next: ExtendedTreeDataNode = {
      ...node,
      isLeaf: !node.hasChildren,
    };

    if (node.children && node.children.length) {
      next.children = ensureIsLeafFlag(node.children as ExtendedTreeDataNode[]);
      next.isLeaf = false;
    }

    return next;
  });

const getTreeNodeLevel = (nodeData: ExtendedTreeDataNode): number => {
  if (nodeData.isRootNode) {
    return 0;
  }

  if (typeof nodeData.depth === 'number' && Number.isFinite(nodeData.depth)) {
    return Math.max(0, nodeData.depth);
  }

  const pos = (nodeData as { pos?: string }).pos;
  if (typeof pos === 'string' && pos.length > 0) {
    return Math.max(0, pos.split('-').length - 2);
  }

  return nodeData.isRootNode ? 0 : 1;
};

const CategoriesMenu: React.FC<CategoriesMenuProps> = ({
  variant = 'card',
  withDrawer = false,
}) => {
  const dispatch = useAppDispatch();
  const initialized = useSelector((state: RootState) => state.eshop.initialized);
  const isCategoriesLoading = useSelector((state: RootState) => state.eshop.isCategoriesLoading);
  const categories = useSelector((state: RootState) => state.eshop.data.categories);
  const searchPrompt = useSelector((state: RootState) => state.eshop.data.searchPrompt);
  const orderBy = useSelector((state: RootState) => state.eshop.data.orderBy);
  const orderByOptions = useSelector((state: RootState) => state.eshop.data.orderByOptions);
  const selectedCategory = useSelector((state: RootState) => state.eshop.data.selectedCategory);
  const { resolvedTheme } = useThemeMode();
  const searchIconColor = resolvedTheme === 'dark' ? '#e2e8f0' : '#1f1f1f';
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [selectedKeysState, setSelectedKeys] = useState<React.Key[]>([]);
  const [debouncedSearchPrompt] = useDebounce(searchPrompt, 1000);
  const navigate = useNavigate();
  const drawerContext = useCategoriesMenuDrawer();
  const { isOpen, closeDrawer, drawerId, drawerTitleId } = drawerContext;
  const isMobile = useMediaQuery('(max-width: 991px)');
  const searchInputRef = useRef<InputRef | null>(null);
  const drawerBodyRef = useRef<HTMLDivElement | null>(null);
  const fetchingNodesRef = useRef<Set<number>>(new Set());
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    logInfo('CategoriesMenu mounted.');
    return () => logInfo('CategoriesMenu unmounted.');
  }, []);

  useEffect(() => {
    if (!initialized) {
      dispatch(setInitialized(true));
      dispatch(fetchCategoriesAsync({ depth: 1 })).then(() => {
        dispatch(fetchFallbackImagesAsync({ depth: 0 }));
      });
    }
  }, [initialized, dispatch]);

  useEffect(() => {
    if (hasFetchedRef.current) {
      dispatch(fetchItemsAsync({}));
    } else {
      hasFetchedRef.current = true;
    }
  }, [orderBy, debouncedSearchPrompt, dispatch]);

  const rawTreeData = useMemo(
    () => groupDuplicateRootNames(transformCategoriesToTreeData(categories)),
    [categories],
  );
  const treeData = useMemo(() => ensureIsLeafFlag(rawTreeData), [rawTreeData]);

  const maybeFetchChildren = useCallback((node: ExtendedTreeDataNode) => {
    const keyNum = Number(node.key);
    const needsLoad = Number.isFinite(keyNum)
      && node.hasChildren
      && !(node.children && node.children.length > 0);

    if (!needsLoad || fetchingNodesRef.current.has(keyNum)) {
      return;
    }

    fetchingNodesRef.current.add(keyNum);

    dispatch(fetchCategoriesAsync({ node_id: keyNum }))
      .finally(() => fetchingNodesRef.current.delete(keyNum));
  }, [dispatch]);

  const onSelect: TreeProps<ExtendedTreeDataNode>['onSelect'] = (selectedKeys, info) => {
    if (selectedKeys.length === 0) {
      return;
    }

    setSelectedKeys(selectedKeys);
    const key = Number(selectedKeys[0]);

    if (!Number.isFinite(key)) {
      return;
    }

    setExpandedKeys((prev) => Array.from(new Set([...prev, key.toString()])));
    dispatch(setCategorySelected(key));

    const path = buildCategoryPath(categories, key);
    if (path) {
      navigate(`/eshop/category/${path}`);
    }

    maybeFetchChildren(info.node);
  };

  const onExpand: TreeProps<ExtendedTreeDataNode>['onExpand'] = (newExpandedKeys, info) => {
    setExpandedKeys(newExpandedKeys as string[]);

    if (info.expanded) {
      maybeFetchChildren(info.node);
    }
  };

  const resetFilters = () => {
    dispatch(setCategorySelected(null));
    setSelectedKeys([]);
    navigate('/eshop/catalogue');
  };

  const renderTreeNodeTitle = (nodeData: ExtendedTreeDataNode, compact = false) => {
    const title = nodeData.title as string;
    const imgUrl = buildUrl(nodeData.img_url);
    const isRootNode = nodeData.isRootNode === true;
    const level = getTreeNodeLevel(nodeData);
    const nodeStyle = {
      '--catalogue-menu-tree-level': level,
      '--catalogue-menu-tree-indent': `${level * 1.15}rem`,
    } as React.CSSProperties;

    /* Compact mode: used inside the mobile drawer */
    if (compact && isRootNode) {
      return (
        <Tooltip title={stripHtml(nodeData.description)} placement="right">
          <div className="catalogue-menu__tree-node catalogue-menu__tree-node--root" style={nodeStyle}>
            <img
              src={imgUrl}
              alt={title}
              className="catalogue-menu__tree-node-img"
            />
            <span className="catalogue-menu__tree-node-label">{title}</span>
            <RightOutlined className="catalogue-menu__tree-node-chevron" />
          </div>
        </Tooltip>
      );
    }

    if (compact) {
      return (
        <Tooltip title={stripHtml(nodeData.description)} placement="right">
          <span className="catalogue-menu__tree-node catalogue-menu__tree-node--child" style={nodeStyle}>
            {title}
          </span>
        </Tooltip>
      );
    }

    /* Default mode: desktop sidebar */
    const titleClassName = `${isRootNode ? 'text-uppercase' : ''} my-1 font-weight-bold fw-bold d-flex flex-column w_200px w_md_150px`;

    return (
      <Tooltip title={stripHtml(nodeData.description)}>
        <div className={titleClassName}>
          {isRootNode && (
            <img
              src={imgUrl}
              alt={title}
              className="rounded-circle w_100px h_100px img_cover mx-auto"
            />
          )}
          <div className={`${isRootNode ? 'mx-auto' : 'text-start'}`}>{title}</div>
        </div>
      </Tooltip>
    );
  };

  useEffect(() => {
    if (!withDrawer || !isOpen || !drawerBodyRef.current) {
      return;
    }

    const focusableSelectors = [
      'button:not([disabled])',
      '[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    const node = drawerBodyRef.current.querySelector<HTMLElement>(focusableSelectors);
    const target = searchInputRef.current?.input ?? node;

    if (target) {
      target.focus({ preventScroll: true });
    }
  }, [isOpen, withDrawer]);

  const renderTree = (className: string, style?: React.CSSProperties, compact = false) => (
    isCategoriesLoading ? (
      <Skeleton paragraph={{ rows: 4 }} active={isCategoriesLoading} />
    ) : (
      <Tree<ExtendedTreeDataNode>
        switcherIcon={compact ? null : <DownOutlined />}
        expandedKeys={expandedKeys}
        selectedKeys={selectedKeysState}
        onSelect={onSelect}
        onExpand={onExpand}
        treeData={treeData}
        titleRender={(node) => renderTreeNodeTitle(node, compact)}
        className={className}
        style={style}
      />
    )
  );

  const renderSoftCommerceContent = (searchRef?: React.Ref<InputRef>, inDrawer = false) => (
    <Layout className={`catalogue-menu${inDrawer ? ' catalogue-menu--drawer' : ''}`} role="navigation" aria-labelledby={drawerTitleId}>
      <div className="catalogue-menu__header">
        <h2 id={drawerTitleId}>Filtrar resultados</h2>
        <p className="catalogue-menu__description">
          Refiná la búsqueda por categoría{inDrawer && <br />}
          {' '}o ajustá el ordenamiento.
        </p>
      </div>

      <div className="catalogue-menu__section">
        <Search
          placeholder="Buscar..."
          value={searchPrompt || ''}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            dispatch(setSearchPrompt(event.target.value))
          }
          enterButton={(
            <Button
              type="text"
              icon={<SearchOutlined className="catalogue-menu__search-icon" />}
            />
          )}
          allowClear
          variant={inDrawer ? 'outlined' : 'borderless'}
          className="catalogue-menu__search"
          ref={searchRef}
        />
      </div>

      {/* Ordenar por */}
      <div className="catalogue-menu__section">
        {inDrawer && (
          <span className="catalogue-menu__section-label">Ordenar por</span>
        )}
        <Select
          placeholder="Ordenar por"
          value={orderBy}
          options={orderByOptions}
          onChange={(value) => dispatch(setOrderby(value))}
          className="catalogue-menu__select"
          {...(inDrawer ? { suffixIcon: <DownOutlined />, prefix: <ClockCircleOutlined className="catalogue-menu__select-prefix" /> } : {})}
        />
      </div>

      {/* Ver todo el catalogo / active filter */}
      <div className="catalogue-menu__section catalogue-menu__active-filters">
        {selectedCategory ? (
          <Tag
            className="catalogue-menu__active-tag"
            closable
            closeIcon={<CloseCircleFilled />}
            onClose={(e) => { e.preventDefault(); resetFilters(); }}
          >
            {selectedCategory.name}
          </Tag>
        ) : inDrawer ? (
          <button
            type="button"
            onClick={resetFilters}
            className="catalogue-menu__catalogue-link"
          >
            <AppstoreOutlined className="catalogue-menu__catalogue-link-icon" />
            <span>Ver todo el catálogo</span>
            <RightOutlined className="catalogue-menu__catalogue-link-chevron" />
          </button>
        ) : (
          <Button
            onClick={resetFilters}
            type="link"
            block
            className="catalogue-menu__reset"
          >
            Ver todo el catálogo
          </Button>
        )}
      </div>

      {/* Categorias */}
      <div className="catalogue-menu__tree">
        {inDrawer && (
          <span className="catalogue-menu__section-label">Categorías</span>
        )}
        {renderTree(
          `catalogue-menu__tree-view${inDrawer ? ' catalogue-menu__tree-view--compact' : ''}`,
          undefined,
          true,
        )}
      </div>
    </Layout>
  );

  const renderCardContent = (searchRef?: React.Ref<InputRef>) => (
    <Card
      variant="borderless"
      className={`catalogue-filters-card theme-${resolvedTheme}`}
      styles={{ body: { padding: 24, height: '100%' } }}
      data-theme={resolvedTheme}
    >
      <div className="catalogue-filters-card__header">
        <Typography.Title level={4} className="mb-0">Explora por categoría</Typography.Title>
        <Typography.Text type="secondary">
          Utiliza los filtros para encontrar exactamente lo que necesitas.
        </Typography.Text>
      </div>

      <Space orientation="vertical" size={20} className="w-100">
        <Search
          placeholder="Buscar productos o categorías"
          value={searchPrompt || ''}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            dispatch(setSearchPrompt(event.target.value));
          }}
          enterButton={(
            <Button
              type="text"
              icon={<SearchOutlined className="fw-bold" style={{ fontSize: 18, color: searchIconColor }} />}
            />
          )}
          allowClear
          variant="borderless"
          className="catalogue-filter-search"
          ref={searchRef}
        />

        <Select
          placeholder="Ordenar por"
          value={orderBy}
          options={orderByOptions}
          onChange={(value) => {
            dispatch(setOrderby(value));
          }}
          className="catalogue-filter-select"
          size="large"
        />
      </Space>

      <Divider className="catalogue-filters-card__divider" />

      <Space orientation="vertical" size={12} className="w-100">
        <Space wrap size={[8, 8]}>
          <Tag color="blue">
            {selectedCategory ? `Categoría activa: ${selectedCategory.name}` : 'Sin categoría seleccionada'}
          </Tag>
          {searchPrompt && (
            <Tag color="purple">Búsqueda: “{searchPrompt}”</Tag>
          )}
          {orderBy && (
            <Tag color="cyan">
              Orden: {orderByOptions.find((opt) => opt.value === orderBy)?.label}
            </Tag>
          )}
        </Space>

        <Button
          onClick={resetFilters}
          type="primary"
          block
          className="catalogue-filter-reset"
        >
          Ver todo el catálogo
        </Button>
      </Space>

      <div className="catalogue-filter-tree mt-3">
        {renderTree('catalogue-filter-tree__list', { marginLeft: 0 })}
      </div>
    </Card>
  );

  if (!withDrawer) {
    return variant === 'soft-commerce' ? renderSoftCommerceContent() : renderCardContent();
  }

  const isSoftCommerce = variant === 'soft-commerce';
  const renderContent = (searchRef?: React.Ref<InputRef>, inDrawer = false) =>
    isSoftCommerce ? renderSoftCommerceContent(searchRef, inDrawer) : renderCardContent(searchRef);
  const drawerRootClassName = isSoftCommerce
    ? `eshop-layout catalogue-design--soft-commerce theme-${resolvedTheme}`
    : undefined;
  const drawerWidth = Math.min(
    typeof window !== 'undefined' ? window.innerWidth * 0.82 : 420,
    430,
  );

  return (
    <>
      <Drawer
        placement="left"
        width={drawerWidth}
        open={isMobile && isOpen}
        onClose={closeDrawer}
        className={isSoftCommerce ? 'catalogue-menu__drawer' : 'catalogue-filters-drawer'}
        rootClassName={drawerRootClassName}
        styles={{ body: { padding: 0 }, header: { display: 'none' } }}
        closable={false}
        destroyOnHidden={false}
        maskClosable
        keyboard
        aria-labelledby={drawerTitleId}
        id={drawerId}
        afterOpenChange={(open) => {
          if (!open) {
            return;
          }
          const target = searchInputRef.current?.input;
          if (target) {
            target.focus({ preventScroll: true });
          }
        }}
      >
        <div
          ref={drawerBodyRef}
          className={isSoftCommerce ? 'catalogue-menu__drawer-body' : undefined}
        >
          {/* Handle pill + close button */}
          {isSoftCommerce && (
            <div className="catalogue-menu__drawer-topbar">
              <div className="catalogue-menu__drawer-handle" />
              <button
                type="button"
                className="catalogue-menu__drawer-close-btn"
                onClick={closeDrawer}
                aria-label="Cerrar filtros"
              >
                <CloseOutlined />
              </button>
            </div>
          )}

          {renderContent(searchInputRef, true)}
        </div>
      </Drawer>
      <div className="catalogue-menu__desktop" aria-hidden={isMobile}>
        {renderContent()}
      </div>
    </>
  );
};

export default CategoriesMenu;
